/**
 * Onboarding State Service
 *
 * Manages the 3-step onboarding flow state in localStorage.
 * Handles page refreshes, browser navigation, and recovery from errors.
 *
 * State is automatically cleared after completion or expiration (1 hour).
 */

const STORAGE_KEY = 'ranktivity_onboarding_state';
const STATE_TTL = 60 * 60 * 1000; // 1 hour expiration

function isLocalStorageAvailable() {
  try {
    if (typeof window === 'undefined') return false;
    const test = '__ls_test__';
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Save onboarding state
 * @param {Object} state
 * @param {string} state.orgId
 * @param {string} state.websiteId
 * @param {string} state.engineId
 * @param {string} state.discoveryUrlId
 * @param {string} state.email
 * @param {string} state.website
 * @param {number} state.step - Current step (1-4)
 * @param {Object} [state.planFeatures]
 * @param {string[]} [state.completedSteps] - e.g. ['identity', 'landscape']
 * @param {number} [state.peersAdded]
 * @param {number} [state.signalsTuned] - Number of high-priority signals configured in Radar step
 * @param {boolean} [state.signalsConfigured]
 * @param {number} [state.completedAt]
 */
export function saveOnboardingState(state) {
  if (!isLocalStorageAvailable()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...state,
      timestamp: Date.now(),
      version: 2,
    }));
  } catch (error) {
    console.error('Error saving onboarding state:', error);
  }
}

/**
 * Get current onboarding state (null if expired or missing)
 */
export function getOnboardingState() {
  if (!isLocalStorageAvailable()) return null;
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    const state = JSON.parse(data);
    if (Date.now() - state.timestamp > STATE_TTL) {
      clearOnboardingState();
      return null;
    }
    return state;
  } catch {
    clearOnboardingState();
    return null;
  }
}

/**
 * Clear onboarding state (call after completion)
 */
export function clearOnboardingState() {
  if (!isLocalStorageAvailable()) return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/**
 * Merge updates into existing state
 */
export function updateOnboardingState(updates) {
  const current = getOnboardingState();
  if (!current) return;
  saveOnboardingState({ ...current, ...updates });
}

/**
 * React hook for onboarding state
 */
export function useOnboardingState() {
  const state = getOnboardingState();
  return {
    state,
    isInProgress: state !== null,
    currentStep: state?.step || null,
    completedSteps: state?.completedSteps || [],
    save: saveOnboardingState,
    update: updateOnboardingState,
    clear: clearOnboardingState,
  };
}

/** Step name constants */
export const ONBOARDING_STEPS = ['identity', 'landscape', 'focus'];
